<script lang="ts" setup>
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Pagination, Navigation, Autoplay } from 'swiper/modules'
import getWidgetTranslatedMediaUrl from '~/utils/getWidgetTranslatedMediaUrl'
import getWidgetTextTranslation from '~/utils/getWidgetTextTranslation'

import { useStoreConfig } from '~/store/storeConfig'
import type { TranslatableString } from '~/types/widgets/widgetTypes'
import type { Swiper as SwiperClass } from 'swiper/types'

interface Props {
  banners?: {
    slug: TranslatableString
    mobile: TranslatableString
    desktop: TranslatableString
    openLinksInNewTab?: boolean
  }[]
}

const props = defineProps<Props>()
const storeConfig = useStoreConfig()
const nuxtApp = useNuxtApp()
let timeoutId: NodeJS.Timeout | null = null

const swiperRef = ref()
const swiperClass = ref<SwiperClass | null>(null)

function onSwiper(swiper: SwiperClass) {
  swiperClass.value = swiper
}

nuxtApp.hook('page:transition:finish', () => {
  if (swiperClass.value && !swiperClass.value.destroyed && swiperRef.value) {
    // Clear any existing timeout before setting a new one
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    timeoutId = setTimeout(() => {
      if (swiperClass.value && !swiperClass.value.destroyed) {
        swiperClass.value?.autoplay.start()
      }
    }, 100)
  }
})

onBeforeUnmount(() => {
  if (timeoutId) {
    clearTimeout(timeoutId)
  }
})
</script>

<template>
  <div class="banner-slider-content">
    <Swiper
      ref="swiperRef"
      :slides-per-view="1"
      :loop="true"
      :modules="[Pagination, Navigation, Autoplay]"
      :navigation="true"
      :pagination="{ clickable: true }"
      :threshold="5"
      :autoplay="{
        pauseOnMouseEnter: false,
        disableOnInteraction: true,
        delay: 5000,
      }"
      @swiper="onSwiper">
      <SwiperSlide v-for="(banner, index) in props.banners" :key="index">
        <NuxtLink
          :to="getWidgetTextTranslation(banner.slug)"
          :target="banner.openLinksInNewTab ? '_blank' : null"
          class="banner-img flex h-full w-full lg:max-h-[450px]">
          <NuxtImg
            v-if="
              getWidgetTranslatedMediaUrl(
                storeConfig.getIsMobile && !storeConfig.isTablet ? banner.mobile : banner.desktop
              )
            "
            format="webp"
            :src="
              getWidgetTranslatedMediaUrl(
                storeConfig.getIsMobile && !storeConfig.isTablet ? banner.mobile : banner.desktop
              )
            "
            class="w-full h-full"
            height="500" />
        </NuxtLink>
      </SwiperSlide>
    </Swiper>
  </div>
</template>

<style lang="postcss">
.banner-slider-content {
  .swiper {
    padding-bottom: 2.5rem;

    @screen md {
      padding-bottom: 0rem;
    }

    .swiper-slide {
      aspect-ratio: 1/0.58;
      user-select: none;

      @screen md {
        aspect-ratio: 1/0.25;
      }

      img {
        border-radius: theme('vars.borderRadiusHeavy');
      }
    }
  }

  .swiper-button-next,
  .swiper-button-prev {
    top: 45%;
    opacity: 0.75;
    margin: 0 1rem;
    display: none;

    &::after {
      background: theme('colors.white');
      opacity: 0.75;
      color: theme('colors.text2');
      border-color: theme('colors.white');
    }

    &:hover::after {
      opacity: 0.85;
    }
  }

  .swiper-pagination-bullet {
    border: theme('vars.borderMedium');

    &-active {
      background: theme('colors.brand.primary');
      border-color: theme('colors.brand.primary');
    }
  }

  @screen md {
    .swiper-button-next,
    .swiper-button-prev {
      display: flex;
    }
  }
}
</style>
